import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import type { BlobShape } from '@/lib/shapes'

import { Sparkle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Blob } from '@/components/generals/blob'

interface IconArtProps {
    icon: LucideIcon
    shape?: BlobShape
    colorClassName?: string
    accentClassName?: string
    tilt?: 'left' | 'right'
    className?: string
}

// A sticker-style illustration: an icon card resting on a colored die-cut
// shape with a couple of small accents. Size it with `className`.
export const IconArt: FC<IconArtProps> = ({
    icon: Icon,
    shape = 'flower',
    colorClassName = 'text-mint',
    accentClassName = 'text-butter',
    tilt = 'left',
    className
}): ReactNode => {
    return (
        <div aria-hidden='true' className={cn('group/art relative size-40 shrink-0', className)}>
            <Blob
                shape={shape}
                className={cn('absolute inset-0 size-full animate-breathe', tilt === 'right' && '[animation-direction:reverse]', colorClassName)}
            />

            <Blob
                shape='burst'
                className={cn(
                    'absolute top-[2%] size-[26%] drop-shadow-[0_2px_0_rgba(60,13,37,0.3)] animate-spin-slow',
                    tilt === 'left' ? 'right-[2%]' : 'left-[2%]',
                    accentClassName
                )}
            />

            <Sparkle
                className={cn('absolute bottom-[12%] size-[15%] fill-plum text-plum animate-twinkle', tilt === 'left' ? 'left-[8%]' : 'right-[8%]')}
                strokeWidth={1.5}
            />

            <span className={cn('absolute bottom-[30%] size-[6%] rounded-full bg-plum animate-bounce', tilt === 'left' ? 'right-[10%]' : 'left-[10%]')} />

            <div className={cn('absolute inset-0 grid place-items-center animate-bob', tilt === 'right' && '[animation-delay:-1.6s]')}>
                <div
                    className={cn(
                        'grid size-[48%] animate-pop place-items-center rounded-[28%] border-2 border-plum bg-cream shadow-[4px_4px_0_0_var(--color-plum)] transition-[scale] duration-300 group-hover/art:scale-110',
                        tilt === 'right' && '[animation-delay:-1.3s]',
                        tilt === 'left' ? '-rotate-6' : 'rotate-6'
                    )}
                >
                    <Icon className='size-[52%] text-plum' strokeWidth={1.75} />
                </div>
            </div>
        </div>
    )
}
